import React, { useState } from 'react';
import {
  ContinuousMonitor,
  ContinuousMonitorType,
  createContinuousMonitor,
  updateContinuousMonitor,
} from '../api';
import { Callout, Checkbox, Dialog, Input, Select } from './primitives';
import { Field } from './ui';

interface CustomMonitorModalProps {
  projectId: string;
  monitor?: ContinuousMonitor | null;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export const CustomMonitorModal: React.FC<CustomMonitorModalProps> = ({
  projectId,
  monitor,
  isOpen,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(monitor?.name || '');
  const [type, setType] = useState<ContinuousMonitorType>(monitor?.type || 'http');
  const [intervalSeconds, setIntervalSeconds] = useState(monitor?.interval_seconds || 60);
  const [timeoutSeconds, setTimeoutSeconds] = useState(monitor?.timeout_seconds || 10);
  const [enabled, setEnabled] = useState(monitor ? monitor.enabled : true);

  // Target config fields
  const [targetUrl, setTargetUrl] = useState(monitor?.target_config?.url || 'https://');
  const [filePath, setFilePath] = useState(monitor?.target_config?.path || '');
  const [maxAgeMin, setMaxAgeMin] = useState(monitor?.target_config?.max_age_min || 30);
  const [tableName, setTableName] = useState(monitor?.target_config?.table_name || '');
  const [timestampColumn, setTimestampColumn] = useState(monitor?.target_config?.timestamp_column || 'updated_at');
  const [processPattern, setProcessPattern] = useState(monitor?.target_config?.pattern || '');
  const [logPath, setLogPath] = useState(monitor?.target_config?.path || '/var/log/magento.log');
  const [logPattern, setLogPattern] = useState(monitor?.target_config?.pattern || 'CRITICAL|ERROR');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const targetConfig: Record<string, any> = {};
    switch (type) {
      case 'http':
        targetConfig.url = targetUrl;
        break;
      case 'file_watch':
        targetConfig.path = filePath;
        targetConfig.max_age_min = Number(maxAgeMin);
        break;
      case 'database_table_watch':
        targetConfig.table_name = tableName;
        targetConfig.timestamp_column = timestampColumn;
        targetConfig.max_age_min = Number(maxAgeMin);
        break;
      case 'process_watch':
        targetConfig.pattern = processPattern;
        break;
      case 'log':
        targetConfig.path = logPath;
        targetConfig.pattern = logPattern;
        break;
      default:
        break;
    }

    try {
      setLoading(true);
      if (monitor) {
        await updateContinuousMonitor(monitor.id, {
          name,
          target_config: targetConfig,
          interval_seconds: Number(intervalSeconds),
          timeout_seconds: Number(timeoutSeconds),
          enabled,
        });
      } else {
        await createContinuousMonitor(projectId, {
          name,
          type,
          target_config: targetConfig,
          interval_seconds: Number(intervalSeconds),
          timeout_seconds: Number(timeoutSeconds),
          enabled,
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save monitor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      title={monitor ? 'Edit custom monitor' : 'Create a custom monitor'}
      onClose={onClose}
      busy={loading}
      onSubmit={handleSubmit}
      footer={
        <>
          <button type="button" className="button button--secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={loading}>
            {loading ? 'Saving' : monitor ? 'Save monitor' : 'Create monitor'}
          </button>
        </>
      }
    >
      {error ? <Callout tone="danger" title="The monitor was not saved">{error}</Callout> : null}

      <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="ERP inbound order sync" /></Field>

      {!monitor && (
        <Field label="What it checks">
          <Select value={type} onChange={(e) => setType(e.target.value as ContinuousMonitorType)}>
            <option value="http">An HTTP or REST endpoint answers</option>
            <option value="file_watch">A file is fresh</option>
            <option value="database_table_watch">A database table is fresh</option>
            <option value="process_watch">A process is running</option>
            <option value="log">A log has no new errors</option>
          </Select>
        </Field>
      )}

      {type === 'http' && <Field label="URL"><Input value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} type="url" required placeholder="https://myshop.com/health_check.php" /></Field>}

      {type === 'file_watch' && (
        <>
          <Field label="File path"><Input value={filePath} onChange={(e) => setFilePath(e.target.value)} required placeholder="/var/log/erp_sync.status" className="input--mono" /></Field>
          <Field label="Maximum age (minutes)"><Input type="number" value={maxAgeMin} onChange={(e) => setMaxAgeMin(Number(e.target.value))} min={1} /></Field>
        </>
      )}

      {type === 'database_table_watch' && (
        <>
          <Field label="Table"><Input value={tableName} onChange={(e) => setTableName(e.target.value)} required placeholder="sales_order" className="input--mono" /></Field>
          <Field label="Timestamp column"><Input value={timestampColumn} onChange={(e) => setTimestampColumn(e.target.value)} placeholder="updated_at" className="input--mono" /></Field>
          <Field label="Maximum age (minutes)"><Input type="number" value={maxAgeMin} onChange={(e) => setMaxAgeMin(Number(e.target.value))} min={1} /></Field>
        </>
      )}

      {type === 'process_watch' && (
        <Field label="Process name or pattern">
          <Input value={processPattern} onChange={(e) => setProcessPattern(e.target.value)} required placeholder="bin/magento queue:consumers:start" className="input--mono" />
        </Field>
      )}

      {type === 'log' && (
        <>
          <Field label="Log file path"><Input value={logPath} onChange={(e) => setLogPath(e.target.value)} required placeholder="/var/log/system.log" className="input--mono" /></Field>
          <Field label="Error pattern (regular expression)">
            <Input value={logPattern} onChange={(e) => setLogPattern(e.target.value)} required placeholder="CRITICAL|ERROR|Exception" className="input--mono" />
          </Field>
        </>
      )}

      <div className="form-grid form-grid--2">
        <Field label="Every (seconds)"><Input type="number" value={intervalSeconds} onChange={(e) => setIntervalSeconds(Number(e.target.value))} min={10} max={3600} /></Field>
        <Field label="Timeout (seconds)"><Input type="number" value={timeoutSeconds} onChange={(e) => setTimeoutSeconds(Number(e.target.value))} min={1} max={60} /></Field>
      </div>

      <Checkbox
        checked={enabled}
        onChange={(e) => setEnabled(e.target.checked)}
        label="Run this monitor on its schedule"
      />
    </Dialog>
  );
};
